import { useFetch } from "../common/AckFetch";

/** An interface for manipulating resources from a resource. */
export interface ResourceApi {
  /** Gets the resource specification. */
  getSpec(name: string): Promise<any[]>;

  /** Queries the resource according to different parameters. */
  query(name: string, filter: object, statsField?: string): Promise<any>;

  /** Gets the resource. */
  get(name: string, suffix: string): Promise<any>;

  /** Gets the group values for the resource. */
  getGroupValues(name: string, groupField: string, transformSpec?: string): Promise<any>;

  /** Gets a list of resources. */
  getValues(name: string): Promise<any[]>;

  /** Gets a resource by ID. */
  getValue(name: string, id: string): Promise<any>;

  /** Posts a resource to the server. */
  postValue(name: string, model: object): Promise<any>;

  /** Patches a resource on the server. */
  patchValue(name: string, model: object, id: string): Promise<any>;

  /** Deletes a resource from the server. */
  deleteValue(name: string, id: string): Promise<any>;
}

/** A hook for manipulating resources from a resource. */
export const useResourceApi = (path: string): ResourceApi => {
  const ackFetch = useFetch(path);
  return {
    query: (name: string, filter: object, statsField?: string) => {
      const searchParams = new URLSearchParams();
      const filterParam = Object.keys(filter || {}).map(key => `${key}:${filter[key]}`).join("|");
      searchParams.append("filterSpec", btoa(filterParam));
      if (statsField) {
        searchParams.append("statsField", statsField);
      }
      return ackFetch.get(`${name}/query/?${searchParams.toString()}`);
    },

    get: (name: string, suffix: string) => ackFetch.get(`${name}/${suffix}/`),

    getSpec: (name: string) => ackFetch.get(`${name}/spec/`),

    getGroupValues: (name: string, groupField: string, transformSpec?: string) => {
      const queryParams = new URLSearchParams();
      queryParams.append("groupField", groupField);
      if (transformSpec) {
        queryParams.append("transformSpec", btoa(transformSpec));
      }
      return ackFetch.get(`${name}/group/?${queryParams.toString()}`);
    },

    getValues: (name: string) => ackFetch.get(`${name}/`),

    getValue: (name: string, id: string) => ackFetch.get(`${name}/${id}/`),

    postValue: (name: string, model: object) => ackFetch.post(`${name}/`, model),

    patchValue: (name: string, model: object, id: string) => ackFetch.patch(`${name}/${id}/`, model),

    deleteValue: (name: string, id: string) => ackFetch.delete(`${name}/${id}/`),
  };
};
